import React from 'react';
import { Alert, Card, List, Space, Tag, Typography } from 'antd';
import { CheckCircleOutlined, PictureOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { TPuzzleLessonConfig } from '../../../models/courses/puzzleLesson';
import './PuzzleLessonTeacher.css';

const { Title, Text } = Typography;

interface PuzzleLessonTeacherProps {
  config: TPuzzleLessonConfig;
  statement?: string;
}

const PuzzleLessonTeacher: React.FC<PuzzleLessonTeacherProps> = ({ config, statement }) => {
  const { t } = useTranslation();

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Alert
        message={t('puzzleLesson.teacher.previewTitle') || 'Vue enseignant - Test de la leçon'}
        description={t('puzzleLesson.teacher.previewDescription') || 'Testez la prompt comme un élève et consultez la configuration.'}
        type="info"
        showIcon
      />

      {/* Final render (all components visible) */}
      <Card title={t('puzzleLesson.teacher.finalRender') || 'Rendu final'} className="puzzle-teacher-card">
        {config.backgroundImage ? (
          <div style={{ textAlign: 'center' }}>
            <div className="puzzle-teacher-final-board">
              <img src={config.backgroundImage} alt="background" className="puzzle-background-image" />
              {(config.components || []).map((component) => (
                <div
                  key={component.id}
                  className="puzzle-teacher-piece"
                  style={{
                    left: `${component.x}%`,
                    top: `${component.y}%`,
                    width: `${component.width}%`,
                    height: `${component.height}%`,
                    backgroundImage: component.image ? `url(${component.image})` : undefined,
                  }}
                  title={`${component.keyword} — x:${component.x}% y:${component.y}%`}
                />
              ))}
            </div>
          </div>
        ) : (
          <Alert type="warning" showIcon message={t('puzzleLesson.teacher.noBackground') || 'Aucune image configurée.'} />
        )}
      </Card>

      <Card title={t('puzzleLesson.teacher.configuration') || 'Configuration'} className="puzzle-teacher-card">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Title level={5}>{t('puzzleLesson.teacher.background') || 'Arrière-plan'}</Title>
            {config.backgroundImage ? (
              <div className="puzzle-teacher-background">
                <img src={config.backgroundImage} alt="background" />
              </div>
            ) : (
              <Alert
                type="warning"
                showIcon
                message={t('puzzleLesson.teacher.noBackground') || 'Aucune image configurée.'}
              />
            )}
          </div>

          <div>
            <Title level={5}>{t('puzzleLesson.teacher.components') || 'Composants'}</Title>
            <List
              dataSource={config.components || []}
              bordered
              renderItem={(item) => (
                <List.Item
                  actions={[
                    item.image ? (
                      <Tag icon={<PictureOutlined />} color="blue" key="image">
                        {t('puzzleLesson.teacher.hasImage') || 'Image'}
                      </Tag>
                    ) : null,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      item.image ? (
                        <div className="puzzle-teacher-thumb" style={{ width: 56, height: 40 }}>
                          <img src={item.image} alt={item.keyword} />
                        </div>
                      ) : undefined
                    }
                    title={(
                      <Space>
                        <Text strong>{item.keyword || t('puzzleLesson.teacher.unknownKeyword') || 'Mot-clé'}</Text>
                        {item.required && (
                          <Tag color="green" icon={<CheckCircleOutlined />}>
                            {t('puzzleLesson.teacher.required') || 'Requis'}
                          </Tag>
                        )}
                      </Space>
                    )}
                    description={`x:${item.x}% y:${item.y}% - ${item.width}x${item.height}%`}
                  />
                </List.Item>
              )}
            />
          </div>
        </Space>
      </Card>
    </Space>
  );
};

export default PuzzleLessonTeacher;
